import {Filter, Snapshot, DBResponse, QueryOptions, FITQMResource, FirebaseResource, JSONObject} from './Types'
import {db} from '../utilities'
import {
  CollectionReference,
  DocumentReference,
  Query,
  QuerySnapshot,
  DocumentSnapshot,
  DocumentData,
  WhereFilterOp,
  OrderByDirection,
  QueryConstraint,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAt,
  endAt,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  writeBatch,
  onSnapshot,
} from 'firebase/firestore'
import { each } from 'lodash'

interface FilterComparators {
  [index: string]: WhereFilterOp
}

interface ConvertQueryResultInfo {
  path: string;
  options: QueryOptions;
}

export default class BaseModel {
  baseQuery: CollectionReference<DocumentData>
  comparisonLookup: FilterComparators = {}
  type: string

  constructor(baseQuery: CollectionReference<DocumentData>, type: string = '') {
    this.baseQuery = baseQuery
    this.type = type
  }

  makeFilter([where_field, comparison, value]: Filter): Filter {
    const comp: WhereFilterOp = comparison
    return [where_field, comp, value]
  }

  normalize(snapshotArray: DocumentSnapshot[]): (DocumentData | undefined)[] {
    return snapshotArray.map((d) => ({...d.data(), id: d.id}))
  }

  denormalize(update: JSONObject): JSONObject {
    console.log('implement the denormalize method')
    return update
  }

  convertQueryResult(
    snapshot: Snapshot,
    callback?: (resources: DBResponse<(JSONObject | undefined)[]>) => void,
    info: ConvertQueryResultInfo | null = null

  ): DBResponse<(JSONObject | undefined)[]> | null {
    let data: (JSONObject | undefined)[] = []
    let dataBundle: DBResponse<(JSONObject | undefined)[]> = {data, ok: false}

    if (snapshot instanceof QuerySnapshot && !snapshot.empty) {
      data = this.normalize(snapshot.docs)
      dataBundle = {data, ok: true}
    } else if (snapshot instanceof DocumentSnapshot && snapshot.exists()) {
      data = this.normalize([snapshot])
      dataBundle = {data, ok: true}
    }

    let dataBundleIds
    if (dataBundle.data.length) {
      dataBundleIds = dataBundle.data.map((each) => ({id: each?.id || null }))
    }

    if (callback) {
      callback(dataBundle)
      return null
    }

    return dataBundle
  }

  makeQuery(
    id: string | undefined,
    options: QueryOptions | undefined
  ): Query | DocumentReference {
    if (id) return doc(this.baseQuery, id)

    if (!options) return this.baseQuery

    const constraints: QueryConstraint[] = []

    if (options.sort) {
      let direction: OrderByDirection | undefined = undefined
      switch (options.direction) {
        case 'asc':
          direction = 'asc'
          break
        case 'desc':
          direction = 'desc'
          break
        default:
          break
      }
      constraints.push(orderBy(options.sort, direction))
    }
    if (options.filters) {
      options.filters.forEach((filter) => {
        const [f, comp, val] = this.makeFilter(filter)
        constraints.push(where(f, comp, val))
      })
    }
    if (options.limit) constraints.push(limit(options.limit))
    if (options.startAt) constraints.push(startAt(options.startAt))
    if (options.endAt) constraints.push(endAt(options.endAt))

    return query(this.baseQuery, ...constraints)
  }

  async get(
    id: string | undefined,
    options: QueryOptions | undefined
  ): Promise<DBResponse<(JSONObject | undefined)[]> | null> {
    const q = this.makeQuery(id, options)
    let snapshot: Snapshot
    try {
      if (q instanceof DocumentReference) {
        snapshot = await getDoc(q)
      } else {
        const qQuery: any = q
        snapshot = await getDocs(qQuery)
      }
    } catch (e) {
      console.log(`failed to get resource. error: `, this.baseQuery.path, e)
      return {data: [], ok: false}
    }
    return this.convertQueryResult(snapshot)
  }

  async update(id: string, options: QueryOptions | null, update: FITQMResource): Promise<boolean> {
    let data: FirebaseResource = this.denormalize(update)
    try {
      await setDoc(doc(this.baseQuery, id), data, {merge: (options || {}).noMerge ? false : true})
      return true
    } catch (e) {
      console.log(`failed to update resource. error: `, this.baseQuery.path, e)
      return false
    }
  }

  async updatebatch(id: string, options: QueryOptions | null, update: FITQMResource): Promise<boolean> {
    const batch = writeBatch(db)
    let data: FirebaseResource = this.denormalize(update)
    try {
      const docRef = doc(this.baseQuery, id)
      batch.set(docRef, data, {merge: (options || {}).noMerge ? false : true})
      await batch.commit()
      return true
    } catch (e) {
      console.log(`Failed to update resource. Error:`, this.baseQuery.path, e)
      return false
    }
  }

  async create(
    options: QueryOptions = {},
    obj: FITQMResource,
    callback?: (obj: JSONObject) => Promise<void>
  ): Promise<string | void> {
    let data: FirebaseResource = this.denormalize(obj)
    const id: string = this.makeId()
    try {
      if (callback) await callback({id, ...data})
      await setDoc(doc(this.baseQuery, id), data)
      return id
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e)
      console.log(`failed to create resource ${JSON.stringify(data)}. error: ${errorMessage}`)
      return
    }
  }

  async delete(id: string): Promise<boolean> {
    try {
      await deleteDoc(doc(this.baseQuery, id))
      return true
    } catch (e) {
      console.log(`failed to delete resource ${id}`)
      return false
    }
  }

  makeId() {
    return doc(this.baseQuery).id
  }

  docRef(id: string): DocumentReference {
    return doc(this.baseQuery, id)
  }

  subscribe(
    id: string = '',
    options: QueryOptions,
    callback: (results: DBResponse<JSONObject | undefined>) => void,
    errorCallback: (error: Error) => void
  ): () => void {
    const q = this.makeQuery(id, options)
    console.info('Subscribe - ', this.baseQuery.path, id, options)
    if (q instanceof DocumentReference) {
      return onSnapshot(q, (snapshot) => this.convertQueryResult(snapshot, callback, { path: this.baseQuery.path, options }), errorCallback)
    } else {
      const qQuery: any = q
      return onSnapshot(qQuery, (snapshot: any) => this.convertQueryResult(snapshot, callback, { path: this.baseQuery.path, options }), errorCallback)
    }
  }
}
